import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import { ErrorState, PageHeader, PageSkeleton } from "../components/Ui";
import { NoClient } from "../components/Workflow";
import { LocationPicker } from "../components/keywords/LocationPicker";
import { ResearchTab } from "../components/keywords/ResearchTab";
import { SavedListTab } from "../components/keywords/SavedListTab";
import { SearchConsoleTab } from "../components/keywords/SearchConsoleTab";
import { RankingsTab } from "../components/keywords/RankingsTab";
import { placeNotes, type KeywordStatus, type Place } from "../keywords";

const TABS = [
  { key: "research", label: "Research" },
  { key: "list", label: "Saved list" },
  { key: "search-console", label: "Search Console" },
  // BrightLocal's Rank Tracker: the same rankings the Reviews page summarises.
  { key: "rankings", label: "Rankings" },
] as const;
type Tab = (typeof TABS)[number]["key"];

export default function Keywords() {
  const { clientId, client } = useClientSelection();
  return (
    <div className="kw-page">
      <PageHeader
        eyebrow="Delivery"
        title="Keywords"
        description={clientId
          ? `Search volume and bids for ${client?.name || "this client"}'s keywords.`
          : "Pick a client to research keywords and keep their list."}
      />
      {clientId ? <KeywordsWorkspace key={clientId} clientId={clientId} /> : <NoClient />}
    </div>
  );
}

function KeywordsWorkspace({ clientId }: { clientId: number }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "superadmin";
  const [params, setParams] = useSearchParams();
  const asked = params.get("tab");
  const tab: Tab = TABS.some((t) => t.key === asked) ? (asked as Tab) : "research";
  const status = useApi<KeywordStatus>(`/api/keywords/${clientId}/status`);
  const [chosen, setChosen] = useState<Place | null>(null);

  if (status.loading) return <PageSkeleton cards={2} />;
  if (status.error || !status.data) return <ErrorState message={status.error || "The Keywords page could not load."} onRetry={status.reload} />;
  const s = status.data;
  const place = chosen || s.default_location;
  const source = s.sources[0];
  const notes = placeNotes(s, tab);
  const setTab = (t: Tab) => setParams((p) => { const next = new URLSearchParams(p); next.set("tab", t); return next; }, { replace: true });

  return (
    <>
      <div className="kw-top">
        <div className="segmented-control kw-tabs" role="tablist" aria-label="Keywords views">
          {TABS.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={tab === t.key ? "active" : ""} onClick={() => setTab(t.key)}>{t.label}</button>
          ))}
        </div>
        {notes.picker && <LocationPicker locations={s.locations} value={place} onChange={setChosen} />}
      </div>
      {tab === "research" && source && !source.ok && (
        <p role="status" className="inline-notice warning">
          <span>
            {source.id === "google_ads" ? "Keyword Planner needs the agency's Google Ads API access." : `${source.label}: ${source.reason}`}{" "}
            {isAdmin ? <Link to="/settings#integrations">Connect it in Settings</Link> : "Ask an admin to connect it in Settings."}
          </span>
        </p>
      )}
      {notes.noCity && (
        <p className="kw-note">
          No city is saved for this client, so numbers are for the whole United States until you pick a place.{" "}
          <Link to={`/clients/${clientId}`}>Add a city on the client's Profile</Link> (Locations) for local bid estimates.
        </p>
      )}
      {notes.unplaced.length > 0 && (
        <p className="kw-note">Google couldn't place {notes.unplaced.join("; ")}. Pick it with "Other place…".</p>
      )}
      {/* Research stays mounted so its results survive a look at the saved list; the others reload when opened. */}
      <div hidden={tab !== "research"}><ResearchTab clientId={clientId} status={s} place={place} /></div>
      {tab === "list" && <SavedListTab clientId={clientId} tags={s.tags} onFind={() => setTab("research")} />}
      {tab === "search-console" && <SearchConsoleTab clientId={clientId} isAdmin={isAdmin} tags={s.tags} place={place} />}
      {tab === "rankings" && <RankingsTab clientId={clientId} isAdmin={isAdmin} />}
    </>
  );
}
